import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges
} from "@angular/core";
import {JwtHttpClientService} from "@fedeltapointofsale/data-client";
import {ChartComponent} from "../../../framework/bi/chart/chart.component";
import {ChartSeries} from "../../../framework/bi/chart/chart-series";
import {MdxMemberUtil} from "../../../framework/bi/mdx-member-util";
import {first, Subject, Subscription, timer} from "rxjs";
import {AggregationType, ChartQueryRequestData} from "../../../framework/bi/chart-query-request.data";
import {DashboardChartData} from "../dashboard.data";
import {BIService} from "../../../framework/bi/bi.service";
import {FlatOlapResultsData, FlatSeriesData} from "../../../framework/bi/flat-olap-results.data";
import {DashboardModel} from "../dashboard.model";
import {FUIResponsiveService} from "@fedeltapointofsale/ui";
import {StringUtils} from "../../../framework/utils/string-utils";

@Component({
    selector: "wbo-bi-dashboard-chart",
    templateUrl: "./dashboard-chart.component.html",
    styleUrls: ["./dashboard-chart.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardChartComponent extends ChartComponent implements OnInit, OnDestroy, OnChanges {

    @Input() chartDef: DashboardChartData;

    @Input() cubeName: string;

    @Input() dashboardChartUpdated: Subject<DashboardChartData>;

    private subscriptions: Subscription = new Subscription();

    private pendingLoading: boolean;

    restricted: boolean;

    lastSeriesData: ChartSeries[];

    runningQuery: boolean;

    runningRequestId: string;
    runningServiceSubscription: Subscription;
    pollSubscription: Subscription;

    drillLevel: number = 0;

    canDrillUp: boolean;
    canDrillDown: boolean;

    constructor(private dashboardModel: DashboardModel,
                private http: JwtHttpClientService,
                private biService: BIService,
                private changeDetectorRef: ChangeDetectorRef,
                private responsiveService: FUIResponsiveService) {
        super();

        this.seriesData = new Subject();

        this.subscriptions.add(this.dashboardModel.dashboardFilter$.subscribe((dashboardFilter) => {
            if (dashboardFilter) {
                this.loadChart();
            }
        }));

        this.subscriptions.add(this.responsiveService.window$.subscribe(() => {
            if (this.lastSeriesData) {
                setTimeout(() => {
                    this.seriesData.next(this.lastSeriesData);
                    setTimeout(() => this.seriesData.next(this.lastSeriesData));
                });
            }
        }));
    }

    ngOnInit(): void {
        if (this.dashboardChartUpdated) {
            this.subscriptions.add(this.dashboardChartUpdated.subscribe((dashboardChartUpdated) => {
                if (this.chartDef === dashboardChartUpdated) {
                    this.loadChart();
                }
            }));
        }

        if (this.chartDef) {
            this.xAxisLabel = this.chartDef.xAxisLabel;
        }
    }

    loadChart(): void {
        if (!this.cubeName || !this.chartDef || this.chartDef.chartType === "blank") {
            this.pendingLoading = true;
            this.changeDetectorRef.markForCheck();
            return;
        }

        if (this.dashboardModel.dashboardFilter.restrictions.datesRestricted
            && !this.dashboardModel.dashboardFilter.restrictions.availableStartDate
            && !this.dashboardModel.dashboardFilter.restrictions.availableEndDate) {
            this.restricted = true;
            return;
        } else {
            this.restricted = false;
        }

        this.cancelRunningRequest();

        const req: ChartQueryRequestData = new ChartQueryRequestData();
        req.requestId = StringUtils.uuid();
        req.dashboardFilterMembers = this.dashboardModel.dashboardFilter.filterMembers.members;
        req.dateRangeRef = this.dashboardModel.dashboardFilter.filterMembers.dateRange;
        req.cubeName = this.cubeName;
        req.chartFilterMembers = this.chartDef.filterMembers;
        req.pinLevel = this.chartDef.pinLevel;
        req.attribute = this.chartDef.attribute;
        req.chartMeasures = this.chartDef.measures;
        req.selectedRefLine = this.chartDef.selectedRefLine;
        req.topBottomRef = this.chartDef.topBottomRef;
        req.topCount = this.chartDef.topCount;
        req.drillLevel = this.drillLevel;

        if (this.chartDef.chartType === "summary") {
            req.aggregationType = AggregationType.SUM;
        }

        if (this.dashboardModel.dashboardFilter.restrictions.datesRestricted) {
            req.restrictedStartDate = this.dashboardModel.dashboardFilter.restrictions.availableStartDate;
            req.restrictedEndDate = this.dashboardModel.dashboardFilter.restrictions.availableEndDate;
            req.restrictedDateLevel = this.dashboardModel.dashboardFilter.restrictions.restrictedDateLevel;
        }

        this.runningRequestId = req.requestId;

        this.runningQuery = true;
        this.changeDetectorRef.markForCheck();

        this.load(req);
    }

    initNextPool(req: ChartQueryRequestData): void {
        this.pollSubscription = timer(3000).pipe(first()).subscribe(() => this.load(req));
    }

    private load(req: ChartQueryRequestData): void {

        if (!this.runningRequestId) {
            return;
        }

        this.runningServiceSubscription = this.biService.queryChart(req).subscribe((queryResponse: FlatOlapResultsData) => {

            if (queryResponse.pending) {
                this.runningServiceSubscription.unsubscribe();
                req.resubmission = true;
                this.initNextPool(req);
                return;
            }

            const seriesArr: ChartSeries[] = [];
            this.canDrillUp = queryResponse.canDrillUp;
            this.canDrillDown = queryResponse.canDrillDown;

            if (queryResponse.attribute) {
                this.xAxisLabel =  queryResponse.attribute.replace(/.*\[(.*)]$/, "$1");
            }

            let selectedMeasures: string[] = this.chartDef.selectedMeasures;
            if (selectedMeasures.length === 0) {
                // No measures selected. Use the measures from the response
                selectedMeasures = [];
                queryResponse.series.forEach((flatSeries: FlatSeriesData) => {
                    if (!selectedMeasures.includes(flatSeries.measure)) {
                        selectedMeasures.push(flatSeries.measure);
                    }
                });
            }

            selectedMeasures.forEach((measure: string, index: number) => {
                queryResponse.series
                    .filter((flatSeries: FlatSeriesData) => flatSeries.measure === measure)
                    .forEach((flatSeries: FlatSeriesData) => {
                        const chartSeries: ChartSeries = new ChartSeries();
                        chartSeries.name = flatSeries.name;
                        chartSeries.data = flatSeries.data;
                        chartSeries.dataType = flatSeries.dataType;
                        chartSeries.attribute = queryResponse.attribute;
                        if (index === 0) {
                            chartSeries.dataMembers = queryResponse.categories;
                            chartSeries.dataLabels = [];
                            chartSeries.dataMembers.forEach((name: string, memberIndex: number) => {
                                if (queryResponse.atTop) {
                                    chartSeries.dataLabels.push("All");
                                } else {
                                    chartSeries.dataLabels.push(MdxMemberUtil.formatForLabel(queryResponse.external[memberIndex]));
                                }
                            });
                        }
                        seriesArr.push(chartSeries);
                    });
            });

            (<Subject<any>>this.seriesData).next(seriesArr);

            this.lastSeriesData = seriesArr;
            this.runningQuery = false;

            this.clearRunningRequest();

            this.changeDetectorRef.markForCheck();
        }, error => {
            this.runningQuery = false;
            this.canDrillUp = false;
            this.canDrillDown = false;

            this.clearRunningRequest();

            this.changeDetectorRef.markForCheck();
            throw error;
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.cancelRunningRequest();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["chartDef"] || changes["cubeName"]) {
            if (this.pendingLoading) {
                this.pendingLoading = false;
                this.loadChart();
            }
        }
    }

    cancelRunningRequest(): void {
        if (this.runningRequestId) {
            this.biService.cancelQuery(this.runningRequestId);
            this.runningServiceSubscription.unsubscribe();

            this.clearRunningRequest();
        }
    }

    clearRunningRequest(): void {
        this.runningRequestId = undefined;
        this.runningServiceSubscription = undefined;

        if (this.pollSubscription) {
            this.pollSubscription.unsubscribe();
            this.pollSubscription = undefined;
        }
    }

    drillDownLevel(): void {
        if (this.canDrillDown) {
            this.drillLevel++;
            this.loadChart();
        }
    }

    drillUpLevel(): void {
        if (this.canDrillUp) {
            this.drillLevel--;
            this.loadChart();
        }
    }

}
